import React, { useState } from "react";
import { useShow, useInvalidate } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Tag, Card, Button, message, Table, Spin, Upload, Tabs } from "antd";
import { UploadOutlined, BankOutlined, EditOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import type { UploadFile } from "antd/es/upload/interface";
import { RcFile } from "antd/lib/upload";
import { api } from "../../api/services";
import { PageHeader } from "../../components/ui";

export const CompanyShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const invalidate = useInvalidate();

    const { queryResult } = useShow({
        resource: "company",
        id,
        meta: {
            fields: [
                "id", "name", "tradeName", "vatNumber", "tpin", "description",
                "taxNumber", "registrationNumber",
                "isActive", "logoUrl", "createdAt", "updatedAt",
                {
                    companyContacts: ["id", "phoneNumber", "email", "isPrimary"],
                    companyAddresses: ["id", "houseNo", "street", "city", "province", "country", "isPrimary"],
                    bankAccounts: ["id", "accountName", "accountNumber", "bankName", "branch", "swiftCode", "isPrimary"]
                }
            ],
        },
    });

    const { data, isLoading } = queryResult;
    const record = data?.data?.value;

    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploading, setUploading] = useState(false);

    const beforeUpload = (file: RcFile) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('You can only upload image files!');
            return false;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Image must be smaller than 5MB!');
            return false;
        }
        setFileList([file]);
        return false;
    };

    const handleUpload = async () => {
        if (!record?.id || fileList.length === 0) return;

        setUploading(true);
        try {
            await api.uploadCompanyLogo(record.id, fileList[0] as RcFile);
            setFileList([]);
            message.success('Logo uploaded successfully');
            invalidate({
                resource: "company",
                invalidates: ["detail"],
                id: record.id,
            });
        } catch (error) {
            console.error('Error uploading logo:', error);
            message.error('Failed to upload logo');
        } finally {
            setUploading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    const contactColumns = [
        { title: 'Phone', dataIndex: 'phoneNumber', key: 'phoneNumber' },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        {
            title: 'Primary',
            dataIndex: 'isPrimary',
            key: 'isPrimary',
            render: (isPrimary: boolean) => (
                <Tag color={isPrimary ? "success" : "default"}>{isPrimary ? "Yes" : "No"}</Tag>
            )
        },
    ];

    const addressColumns = [
        { title: 'House No', dataIndex: 'houseNo', key: 'houseNo' },
        { title: 'Street', dataIndex: 'street', key: 'street' },
        { title: 'City', dataIndex: 'city', key: 'city' },
        { title: 'Province', dataIndex: 'province', key: 'province', responsive: ['md'] as const },
        { title: 'Country', dataIndex: 'country', key: 'country', responsive: ['lg'] as const },
        {
            title: 'Primary',
            dataIndex: 'isPrimary',
            key: 'isPrimary',
            render: (isPrimary: boolean) => (
                <Tag color={isPrimary ? "success" : "default"}>{isPrimary ? "Yes" : "No"}</Tag>
            )
        },
    ];

    const bankAccountColumns = [
        { title: 'Account Name', dataIndex: 'accountName', key: 'accountName' },
        { title: 'Account No', dataIndex: 'accountNumber', key: 'accountNumber' },
        { title: 'Bank', dataIndex: 'bankName', key: 'bankName' },
        { title: 'Branch', dataIndex: 'branch', key: 'branch', responsive: ['md'] as const },
        { title: 'Swift', dataIndex: 'swiftCode', key: 'swiftCode', responsive: ['lg'] as const },
        {
            title: 'Primary',
            dataIndex: 'isPrimary',
            key: 'isPrimary',
            render: (isPrimary: boolean) => (
                <Tag color={isPrimary ? "success" : "default"}>{isPrimary ? "Yes" : "No"}</Tag>
            )
        },
    ];

    const tabItems = [
        {
            key: 'overview',
            label: 'Overview',
            children: (
                <div className="space-y-4">
                    <div className="two-column-grid">
                        {/* Company Information */}
                        <div className="info-card">
                            <div className="info-card-header">
                                <h3 className="info-card-title">Company Information</h3>
                            </div>
                            <div className="info-card-body">
                                <div className="info-grid">
                                    <div className="info-item">
                                        <span className="info-label">Company Name:</span>
                                        <span className="info-value font-semibold">{record?.name || "-"}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Trade Name:</span>
                                        <span className="info-value">{record?.tradeName || "-"}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">VAT Number:</span>
                                        <span className="info-value">{record?.vatNumber || "-"}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">TPIN:</span>
                                        <span className="info-value">{record?.tpin || "-"}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Tax Number:</span>
                                        <span className="info-value">{record?.taxNumber || "-"}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Registration No:</span>
                                        <span className="info-value">{record?.registrationNumber || "-"}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Status:</span>
                                        <span className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                                            {record?.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                            {record?.isActive ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Created At:</span>
                                        <span className="info-value">
                                            <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                        </span>
                                    </div>
                                    {record?.description && (
                                        <div className="info-item full-width">
                                            <span className="info-label">Description:</span>
                                            <span className="info-value">{record.description}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Logo Card */}
                        <div className="info-card">
                            <div className="info-card-header">
                                <h3 className="info-card-title">Company Logo</h3>
                            </div>
                            <div className="info-card-body text-center">
                                {record?.logoUrl ? (
                                    <div style={{ marginBottom: 16 }}>
                                        <img
                                            src={record.logoUrl}
                                            alt="Company Logo"
                                            style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                                        />
                                    </div>
                                ) : (
                                    <div className="py-8 text-text-muted">No logo uploaded</div>
                                )}
                                <div className="mt-4">
                                    <Upload
                                        listType="picture"
                                        fileList={fileList}
                                        beforeUpload={beforeUpload}
                                        onRemove={() => setFileList([])}
                                        maxCount={1}
                                    >
                                        <Button icon={<UploadOutlined />}>Select New Logo</Button>
                                    </Upload>
                                    <Button
                                        type="primary"
                                        onClick={handleUpload}
                                        disabled={fileList.length === 0}
                                        loading={uploading}
                                        style={{ marginTop: 16 }}
                                    >
                                        {uploading ? 'Uploading' : 'Upload'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'contacts',
            label: 'Contacts',
            children: (
                <Card title="Contacts" className="data-table-card">
                    {record?.companyContacts && record.companyContacts.length > 0 ? (
                        <Table
                            dataSource={record.companyContacts}
                            columns={contactColumns}
                            rowKey="id"
                            pagination={false}
                            className="custom-table"
                            scroll={{ x: 400 }}
                        />
                    ) : (
                        <div className="text-center py-8 text-text-muted">No contact information available</div>
                    )}
                </Card>
            ),
        },
        {
            key: 'addresses',
            label: 'Addresses',
            children: (
                <Card title="Addresses" className="data-table-card">
                    {record?.companyAddresses && record.companyAddresses.length > 0 ? (
                        <Table
                            dataSource={record.companyAddresses}
                            columns={addressColumns}
                            rowKey="id"
                            pagination={false}
                            className="custom-table"
                            scroll={{ x: 600 }}
                        />
                    ) : (
                        <div className="text-center py-8 text-text-muted">No address information available</div>
                    )}
                </Card>
            ),
        },
        {
            key: 'banking',
            label: 'Bank Accounts',
            children: (
                <Card title="Bank Accounts" className="data-table-card">
                    {record?.bankAccounts && record.bankAccounts.length > 0 ? (
                        <Table
                            dataSource={record.bankAccounts}
                            columns={bankAccountColumns}
                            rowKey="id"
                            pagination={false}
                            className="custom-table"
                            scroll={{ x: 700 }}
                        />
                    ) : (
                        <div className="text-center py-8 text-text-muted">No bank account information available</div>
                    )}
                </Card>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title={record?.name || "Company Details"}
                titleIcon={<BankOutlined />}
                breadcrumbs={[
                    { title: "Company", href: "/company" },
                    { title: record?.name || "Details" },
                ]}
                actions={[
                    {
                        label: "Edit",
                        onClick: () => navigate(`/company/edit/${id}`),
                        type: "primary",
                        icon: <EditOutlined />,
                    },
                ]}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Trade Name</div>
                        <div className="mini-stat-value">{record?.tradeName || "-"}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">TPIN</div>
                        <div className="mini-stat-value">{record?.tpin || "-"}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <div className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                            {record?.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            {record?.isActive ? "Active" : "Inactive"}
                        </div>
                    </div>
                </div>
            </PageHeader>

            {/* Tabs */}
            <div className="tab-card">
                <Tabs defaultActiveKey="overview" items={tabItems} className="custom-tabs" />
            </div>
        </div>
    );
};
